import React from 'react';
import {
  getPageRowIds,
  isRowSelected,
  toggleRowSelection,
  mergePageSelection,
  isPageFullySelected,
  isPagePartiallySelected,
  normalizeRowId
} from '../../utils/crossPageSelection';

const CommonDataTable = ({ 
  data = [], 
  columns = [], 
  loading = false,
  onRowClick = null,
  onEdit = null,
  onDelete = null,
  onView = null,
  pagination = null,
  searchTerm = '',
  onSearchChange = null,
  selectedRows = [],
  onRowSelect = null,
  bulkActions = [],
  className = '',
  emptyMessage = 'No data available',
  compact = false,
  customActions = []
}) => {
  const tableSize = compact ? 'text-xs' : 'text-sm';
  const paddingSize = compact ? 'px-2 py-1' : 'px-4 py-3';

  const pageRowIds = getPageRowIds(data);
  const pageFullySelected = isPageFullySelected(selectedRows, pageRowIds);
  const pagePartiallySelected = isPagePartiallySelected(selectedRows, pageRowIds);

  const handleRowClick = (row, index) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  const isRowSelectedState = (rowId) => isRowSelected(selectedRows, rowId);

  const handleSelectAll = (e) => {
    if (onRowSelect) {
      onRowSelect(mergePageSelection(selectedRows, pageRowIds, e.target.checked));
    }
  };

  const handleRowSelect = (rowId, checked) => {
    if (onRowSelect) {
      const newSelection = checked
        ? toggleRowSelection(selectedRows, rowId)
        : selectedRows.filter((id) => normalizeRowId(id) !== normalizeRowId(rowId));
      onRowSelect(newSelection);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-8 text-center">
          <div className="flex items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-2 text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden ${className}`}>
      {/* Search and Bulk Actions */}
      {(onSearchChange || bulkActions.length > 0) && (
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            {onSearchChange && (
              <div className="relative flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            )}
            
            {bulkActions.length > 0 && selectedRows.length > 0 && (
              <div className="flex gap-2">
                {bulkActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => action.onClick(selectedRows)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${action.className || 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {action.icon && <span className="mr-2">{action.icon}</span>}
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-100">
        {data.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293L16 14.586M4 13h2.586a1 1 0 01.707.293L8 14.586" />
              </svg>
              {emptyMessage}
            </div>
          </div>
        ) : (
          data.map((row, rowIndex) => {
            const rowId = row._id || row.id;
            const isSelected = isRowSelectedState(rowId);
            return (
              <div
                key={rowIndex}
                className={`p-4 ${isSelected ? 'bg-green-50' : 'bg-white'} ${onRowClick ? 'cursor-pointer active:bg-gray-50' : ''}`}
                onClick={() => handleRowClick(row, rowIndex)}
              >
                {onRowSelect && (
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => { e.stopPropagation(); handleRowSelect(rowId, e.target.checked); }}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                  </div>
                )}
                <dl className="space-y-1">
                  {columns.map((column, colIndex) => (
                    <div key={colIndex} className="flex justify-between items-start gap-4">
                      <dt className="text-xs font-semibold text-gray-500 tracking-wide shrink-0">{column.header}</dt>
                      <dd className="text-sm text-gray-900 text-right">{column.render ? column.render(row[column.key], row, rowIndex) : row[column.key] || '-'}</dd>
                    </div>
                  ))}
                </dl>
                {(onEdit || onDelete || onView || customActions.length > 0) && (
                  <div className="flex items-center justify-end space-x-1 mt-3 pt-3 border-t border-gray-100">
                    {onView && (
                      <button onClick={(e) => { e.stopPropagation(); onView(row); }} className="text-blue-600 hover:text-blue-800 p-1 rounded-md hover:bg-blue-50" title="View">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      </button>
                    )}
                    {onEdit && (
                      <button onClick={(e) => { e.stopPropagation(); onEdit(row); }} className="text-emerald-600 hover:text-emerald-800 p-1 rounded-md hover:bg-emerald-50" title="Edit">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                    )}
                    {customActions.map((action, actionIndex) => {
                      if (action.show && !action.show(row)) return null;
                      return (
                        <button key={actionIndex} onClick={(e) => { e.stopPropagation(); action.onClick(row, rowIndex); }} className={`${action.color || 'text-gray-600 hover:text-gray-800'} p-1 rounded-md hover:bg-gray-50`} title={action.label}>
                          {action.icon}
                        </button>
                      );
                    })}
                    {onDelete && (
                      <button onClick={(e) => { e.stopPropagation(); onDelete(row); }} className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50" title="Delete">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-auto max-h-[70vh]">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
            <tr>
              {onRowSelect && (
                <th className={`${paddingSize} w-10`}>
                  <input
                    type="checkbox"
                    checked={pageFullySelected}
                    ref={(el) => {
                      if (el) el.indeterminate = pagePartiallySelected;
                    }}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </th>
              )}
              {columns.map((column, index) => (
                <th 
                  key={index}
                  className={`${paddingSize} text-left font-semibold text-gray-800 ${tableSize}`}
                  style={column.width ? { width: column.width } : {}}
                >
                  {column.header}
                </th>
              ))}
              {(onEdit || onDelete || onView || customActions.length > 0) && (
                <th className={`${paddingSize} text-center font-semibold text-gray-800 ${tableSize} w-32`}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.length === 0 ? (
              <tr>
                <td 
                  colSpan={columns.length + (onRowSelect ? 1 : 0) + (onEdit || onDelete || onView || customActions.length > 0 ? 1 : 0)}
                  className="text-center py-8 text-gray-500"
                >
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293L16 14.586M4 13h2.586a1 1 0 01.707.293L8 14.586" />
                    </svg>
                    {emptyMessage}
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const rowId = row._id || row.id;
                const isSelected = isRowSelectedState(rowId);
                
                return (
                  <tr 
                    key={rowIndex}
                    className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-green-50' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => handleRowClick(row, rowIndex)}
                  >
                    {onRowSelect && (
                      <td className={paddingSize}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleRowSelect(rowId, e.target.checked);
                          }}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                      </td>
                    )}
                    {columns.map((column, colIndex) => (
                      <td key={colIndex} className={`${paddingSize} text-gray-900 ${tableSize}`}>
                        {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key] || '-'}
                      </td>
                    ))}
                    {(onEdit || onDelete || onView || customActions.length > 0) && (
                      <td className={`${paddingSize} text-center`}>
                        <div className="flex items-center justify-center space-x-1">
                          {onView && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onView(row); }}
                              className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded-md hover:bg-blue-50"
                              title="View"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          )}
                          {onEdit && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onEdit(row); }}
                              className="text-emerald-600 hover:text-emerald-800 transition-colors p-1 rounded-md hover:bg-emerald-50"
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          
                          {/* Custom Actions */}
                          {customActions.map((action, actionIndex) => {
                            if (action.show && !action.show(row)) return null;
                            
                            return (
                              <button
                                key={actionIndex}
                                onClick={(e) => { e.stopPropagation(); action.onClick(row, rowIndex); }}
                                className={`${action.color || 'text-gray-600 hover:text-gray-800'} transition-colors p-1 rounded-md hover:bg-gray-50`}
                                title={action.label}
                              >
                                {action.icon}
                              </button>
                            );
                          })}

                          {onDelete && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onDelete(row); }}
                              className="text-red-600 hover:text-red-800 transition-colors p-1 rounded-md hover:bg-red-50"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
          {pagination}
        </div>
      )}
    </div>
  );
};

export default CommonDataTable;